<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-building me-2"></i>Organization Details
      </h4>
      <div v-if="!loading && profile.role !== 'user' && profile.role !== 'admin'" class="d-flex">
        <button class="btn btn-primary" @click="showModal = true">
          <i class="bi bi-pencil-square me-1"></i> Edit Details
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading organization details...</p>
      </div>

      <!-- Organization Content -->
      <div v-if="!loading" class="organization-content">
        <!-- Organization Header -->
        <div class="card p-4 mt-3 bg-white org-header mb-4 text-center shadow-sm border-0">
          <h2 class="display-6 fw-bold text-primary mb-2">{{ organization.name }}</h2>
          <p class="text-muted">
            <i class="bi bi-geo-alt me-1"></i> {{ organization.address }}
          </p>
          <div class="badge bg-success-light text-success mt-2 mx-auto">
            <i class="bi bi-check-circle me-1"></i> Active
          </div>
        </div>

        <div class="row g-4">
          <!-- Logo Section -->
          <div class="col-md-4">
            <div class="card h-100 border-0 shadow-sm logo-card">
              <div class="card-body text-center">
                <h5 class="card-title mb-3">
                  <i class="bi bi-image me-2"></i>Organization Logo
                </h5>
                <div class="logo-container mb-4 d-flex justify-content-center align-items-center">
                  <div class="logo-wrapper">
                    <img
                      class="img-fluid rounded"
                      :src="organization.logo"
                      alt="Organization Logo"
                    >
                    <div class="logo-overlay" v-if="profile.role !== 'user' && profile.role !== 'admin'">
                      <button
                        class="btn btn-sm btn-light"
                        @click="$refs.refInputEl.click()"
                      >
                        <i class="bi bi-pencil"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  v-if="profile.role !== 'user' && profile.role !== 'admin'"
                  class="btn btn-primary w-100"
                  @click="$refs.refInputEl.click()"
                >
                  <i class="bi bi-upload me-2"></i>Upload New Logo
                </button>
                <p class="text-muted small mt-2">
                  <i class="bi bi-info-circle me-1"></i>
                  Allowed formats: JPEG, JPG, or PNG. Max size: 2MB
                </p>
                <input
                  ref="refInputEl"
                  type="file"
                  name="logo"
                  accept=".jpeg,.png,.jpg"
                  hidden
                  @input="changeLogo"
                >
              </div>
            </div>
          </div>

          <!-- Contact Information -->
          <div class="col-md-8">
            <div class="card h-100 border-0 shadow-sm info-card">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  <i class="bi bi-info-circle me-2"></i>Contact Information
                </h5>
                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="info-item">
                      <span class="info-label"><i class="bi bi-telephone me-2"></i>Phone</span>
                      <span class="info-value">
                        {{ organization.phone || 'Not provided' }}
                      </span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="info-item">
                      <span class="info-label"><i class="bi bi-envelope me-2"></i>Email</span>
                      <span class="info-value">
                        {{ organization.email || 'Not provided' }}
                      </span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="info-item">
                      <span class="info-label"><i class="bi bi-globe me-2"></i>Website</span>
                      <span class="info-value">
                        {{ organization.website || 'Not provided' }}
                      </span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="info-item">
                      <span class="info-label"><i class="bi bi-card-text me-2"></i>PIN No.</span>
                      <span class="info-value">
                        {{ organization.pin_no || 'Not provided' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Banking Information -->
          <div class="col-md-12">
            <div class="card border-0 shadow-sm info-card">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  <i class="bi bi-bank me-2"></i>Banking Information
                </h5>
                <div class="row g-3">
                  <div class="col-md-4">
                    <div class="info-item">
                      <span class="info-label"><i class="bi bi-building me-2"></i>Bank Name</span>
                      <span class="info-value">
                        {{ organization.bank_name || 'Not provided' }}
                      </span>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="info-item">
                      <span class="info-label"><i class="bi bi-geo me-2"></i>Branch Name</span>
                      <span class="info-value">
                        {{ organization.branch_name || 'Not provided' }}
                      </span>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="info-item">
                      <span class="info-label"><i class="bi bi-credit-card me-2"></i>Account Number</span>
                      <span class="info-value">
                        {{ organization.account_no || 'Not provided' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Commented out subscription section, keeping for reference -->
          <!--
          <div class="col-md-12">
            <div class="card border-0 shadow-sm">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  <i class="bi bi-box me-2"></i>Subscription Details
                </h5>
                <div class="row g-3">
                  <div class="col-md-3">
                    <div class="info-item">
                      <span class="info-label">Package</span>
                      <span class="info-value">{{ subscription.package.name }}</span>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="info-item">
                      <span class="info-label">Employees</span>
                      <span class="info-value">{{ subscription.package.employees === 1000 ? 'Unlimited' : subscription.package.employees }}</span>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="info-item">
                      <span class="info-label">Price</span>
                      <span class="info-value">{{ formatNumber(subscription.package.price) + '/month' }}</span>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="info-item">
                      <span class="info-label">Period</span>
                      <span class="info-value">{{ formatDate(subscription.from) }} - {{ formatDate(subscription.to) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          -->
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-center mt-4 mb-2">
          <button class="btn btn-outline-secondary me-3" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
          <button
            v-if="profile.role !== 'user' && profile.role !== 'admin'"
            class="btn btn-primary"
            @click="showModal = true"
          >
            <i class="bi bi-pencil-square me-1"></i> Edit Details
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Organization Modal -->
    <Modal :open="showModal" @closed="showModal = false" dialog-size="modal-xl">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi bi-building me-2"></i>Edit Organization
        </h5>
        <button @click="showModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <!-- Basic Information Section -->
          <div class="mb-4">
            <h6 class="section-title mb-3">
              <i class="bi bi-info-circle me-2"></i>Basic Information
            </h6>
            <div class="row g-3 border border-2 rounded-3 pb-3">
              <div class="form-group col-md-4">
                <label for="orgName" class="form-label">
                  Organization Name
                </label>
                <input
                  type="text"
                  id="orgName"
                  class="form-control form-control-lg"
                  v-model="organization.name"
                  placeholder="Enter organization name"
                >
              </div>
              <div class="form-group col-md-8">
                <label for="orgAddress" class="form-label">
                  Address
                </label>
                <input
                  type="text"
                  id="orgAddress"
                  class="form-control"
                  v-model="organization.address"
                  placeholder="Enter full address"
                >
                <small class="text-muted">Include street, city, state/province, and postal code</small>
              </div>
            </div>
          </div>

          <!-- Contact Information Section -->
          <div class="mb-4">
            <h6 class="section-title mb-3">
              <i class="bi bi-telephone me-2"></i>Contact Information
            </h6>
            <div class="row g-3 border border-2 rounded-3 pb-3">
              <div class="form-group col-md-4">
                <label for="orgPhone" class="form-label">
                  Phone
                </label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-telephone-fill"></i></span>
                  <input
                    type="text"
                    id="orgPhone"
                    class="form-control"
                    v-model="organization.phone"
                    placeholder="Enter phone number"
                  >
                </div>
              </div>
              <div class="form-group col-md-4">
                <label for="orgEmail" class="form-label">
                  Email
                </label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-envelope-fill"></i></span>
                  <input
                    type="email"
                    id="orgEmail"
                    class="form-control"
                    v-model="organization.email"
                    placeholder="Enter email address"
                  >
                </div>
              </div>
              <div class="form-group col-md-4">
                <label for="orgWebsite" class="form-label">
                  Website <span class="text-muted small">(optional)</span>
                </label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-globe2"></i></span>
                  <input
                    type="text"
                    id="orgWebsite"
                    class="form-control"
                    v-model="organization.website"
                    placeholder="Enter website URL"
                  >
                </div>
              </div>
              <div class="form-group col-md-4">
                <label for="orgPin" class="form-label">
                  PIN No.
                </label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-card-text"></i></span>
                  <input
                    type="text"
                    id="orgPin"
                    class="form-control"
                    v-model="organization.pin_no"
                    placeholder="Enter PIN number"
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Banking Information Section -->
          <div class="mb-4">
            <h6 class="section-title mb-3">
              <i class="bi bi-bank me-2"></i>Banking Information <span class="text-muted small">(optional)</span>
            </h6>
            <div class="row g-3 border border-2 rounded-3 pb-3">
              <div class="form-group col-md-4">
                <label for="bankName" class="form-label">
                  Bank Name
                </label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-building-fill"></i></span>
                  <input
                    type="text"
                    class="form-control"
                    list="bankOptions"
                    id="bankName"
                    v-model="organization.bank_name"
                    placeholder="Type to search..."
                  >
                </div>
                <datalist id="bankOptions">
                  <option v-for="bank in bankNames.banks" :key="bank" :value="bank"></option>
                </datalist>
              </div>
              <div class="form-group col-md-4">
                <label for="branchName" class="form-label">
                  Branch Name
                </label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-geo-alt-fill"></i></span>
                  <input
                    type="text"
                    class="form-control"
                    list="branchOptions"
                    id="branchName"
                    v-model="organization.branch_name"
                    placeholder="Type to search..."
                  >
                </div>
                <datalist id="branchOptions">
                  <option v-for="branch in bankBranches" :key="branch" :value="branch"></option>
                </datalist>
              </div>
              <div class="form-group col-md-4">
                <label for="accountNo" class="form-label">
                  Account Number
                </label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-credit-card-fill"></i></span>
                  <input
                    type="text"
                    id="accountNo"
                    class="form-control"
                    placeholder="Enter account number"
                    v-model="organization.account_no"
                  >
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="showModal = false">
          <i class="bi bi-x-circle me-1"></i>Cancel
        </button>
        <button
          v-if="profile.role !== 'user' && profile.role !== 'admin'"
          type="button"
          class="btn btn-primary"
          @click="submitForm"
        >
          <i class="bi bi-check-circle me-1"></i>Save Changes
        </button>
      </div>
    </Modal>
    <!-- Edit Organization Modal End -->

  </Card>
</template>

<script>
import organizationService from "@/api/organizationService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import formatNumber from "../../utils/formatNumber";
import bankService from "@/api/bankService";

export default {
  name: "Organization",

  data() {
    return {
      profile: {},
      loading: true,
      showModal: false,
      organization: {},
      subscription: {
        package: {}
      },
      bankNames: {
        banks: [],
        branches: {}
      },
      bankBranches: [],
    }
  },

  methods: {
    formatNumber,
    formatDate,
    async getOrganization() {
      this.organization = await organizationService.getOrganization(this.profile.organization_id)
      this.loading = false
    },
    submitForm: async function () {
      let response = await organizationService.updateOrganization(this.organization, this.profile.organization_id)
      if (response) {
        this.showModal = false
        await this.getOrganization()
      }
    },
    changeLogo : async function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      let response = false
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string')
            response = await organizationService.updateOrganizationLogo({ file: fileReader.result }, this.profile.organization_id)
            if (response)
              await this.getOrganization()
        }
      }
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getOrganization()
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
    this.bankNames = await bankService.getBankNames()
    if (this.organization.bank_name !== null) {
      this.bankBranches = this.bankNames.branches[this.organization.bank_name]
    }
  },

  watch: {
    'organization.bank_name': function () {
      this.bankBranches = this.bankNames.branches[this.organization.bank_name]
    }
  }
}
</script>

<style scoped>
/* Organization page styles */
.organization-content {
  padding: 0.5rem;
}

.org-header {
  padding-bottom: 1rem;
  transition: all 0.3s ease;
}

.org-header:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

.section-title {
  color: #495057;
  font-weight: 600;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.info-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.info-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.info-value {
  color: #212529;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.info-value:hover {
  background-color: #eef2ff;
  transform: translateX(5px);
}

/* Logo styles */
.logo-card {
  transition: all 0.3s ease;
}

.logo-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

.logo-wrapper {
  position: relative;
  max-width: 200px;
  max-height: 200px;
  margin: 0 auto;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.logo-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.logo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.logo-wrapper:hover .logo-overlay {
  opacity: 1;
}

/* Info card styles */
.info-card {
  transition: all 0.3s ease;
}

.info-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.5em 0.75em;
  font-size: 0.75em;
  border-radius: 6px;
}

.bg-success-light {
  background-color: #d1fae5;
  color: #10b981;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .org-header h2 {
    font-size: 1.75rem;
  }

  .card-title {
    font-size: 1.1rem;
  }

  .info-value {
    padding: 0.5rem;
  }

  .logo-wrapper {
    max-width: 150px;
    max-height: 150px;
  }
}

/* Animation for content loading */
.organization-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
